/**
 * Header component with navigation and user menu
 */

import { Link, useLocation } from 'react-router-dom';
import { useAuth, /* useUser, */ UserButton } from '@clerk/clerk-react';
import {
  Bars3Icon,
  BellIcon,
} from '@heroicons/react/24/outline';
import { useGetNotificationsQuery } from '@/services/api';
import NotificationsPage from '../NotificationsPage';
import hatchLogo from '../../assets/hatch-logo.svg';

export default function Header() {
  const location = useLocation();
  const { isSignedIn } = useAuth();
  // const { user } = useUser(); // TODO: Uncomment when implementing proper role check
  const { data: notifications } = useGetNotificationsQuery(undefined, {
    skip: !isSignedIn,
  });

  // TODO: Replace with actual admin check when Clerk role sync is working
  const isAdmin = isSignedIn; // Temporary: all logged-in users can access admin
  // const isAdmin = user?.publicMetadata?.role === 'admin';
  const unreadCount = Array.isArray(notifications) ? notifications.filter((n) => !n.is_read).length : 0;

  const navItems = [
    { name: 'Home', path: '/', public: true },
    { name: 'Catalog', path: '/catalog', public: true },
    { name: 'Operating Systems', path: '/os', public: true },
    { name: 'Vulnerabilities', path: '/vulnerabilities', public: true },
    { name: 'Ask Hatch', path: '/ai-assistant', public: false },
    { name: 'API Keys', path: '/api-keys', public: false },
    { name: 'Admin', path: '/admin', public: false, adminOnly: true },
  ];

  return (
    <header className="sticky top-0 z-50 bg-dark-bg-secondary border-b border-gray-800 backdrop-blur-sm bg-opacity-95">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <img src={hatchLogo} alt="H" className="w-8 h-8 opacity-90 group-hover:opacity-100 transition-opacity" style={{ filter: 'brightness(1.15)' }} />
            <span className="text-xl font-bold text-gradient">Hatch Manager</span>
          </Link>

          {/* Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              // Filter nav items based on auth state and role
              if (!item.public && !isSignedIn) return null;
              if (item.adminOnly && !isAdmin) return null;

              const isActive = location.pathname === item.path ||
                             (item.path !== '/' && location.pathname.startsWith(item.path));

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    isActive
                      ? 'bg-cyber-blue-500 text-white shadow-glow-sm'
                      : 'text-gray-300 hover:bg-dark-bg-tertiary hover:text-white'
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-4">
            {/* Notifications */}
            {isSignedIn && (
              <button className="relative p-2 text-gray-400 hover:text-gray-300 rounded-lg hover:bg-dark-bg-tertiary transition-colors">
                <BellIcon className="w-6 h-6" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-severity-critical rounded-full animate-pulse" />
                )}
              </button>
            )}

            {/* User menu */}
            {isSignedIn ? (
              <UserButton
                appearance={{
                  elements: {
                    avatarBox: 'w-9 h-9',
                  },
                }}
                afterSignOutUrl="/"
                userProfileProps={{
                  additionalOAuthScopes: {},
                  appearance: {},
                }}
              >
                <UserButton.UserProfilePage
                  label="Notifications"
                  labelIcon={<BellIcon className="w-4 h-4" />}
                  url="custom-notifications"
                >
                  <NotificationsPage />
                </UserButton.UserProfilePage>
              </UserButton>
            ) : (
              <Link to="/sign-in" className="btn-primary">
                Sign In
              </Link>
            )}

            {/* Mobile menu button */}
            <button className="md:hidden p-2 text-gray-400 hover:text-gray-300 rounded-lg hover:bg-dark-bg-tertiary">
              <Bars3Icon className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
